<!--
TriggerBindingForm — a vinculação de UM slot (gatilho x tipo de canal):
qual canal, qual template, quem recebe, ligada ou não.

Lei anti-mentira preservada da TriggerMatrixSection: `implemented=false`
desabilita SÓ o botão de ligar (com a etiqueta "em breve"). O staff pode
salvar a vinculação desligada antes de o emissor existir; ligá-la é que o
backend recusa com 'trigger_not_implemented'.

Uma recusa NUNCA deixa o toggle mostrando o clique otimista — volta para o
último valor que o servidor confirmou (mesma lei do ScheduledJobsPage T4b).
-->
<template>
  <div class="trigger-binding-form" data-testid="trigger-binding-form">
    <p class="trigger-binding-vars" data-testid="trigger-binding-vars">
      {{ t('Variáveis disponíveis') }}: {{ varsDisplay }}
    </p>

    <input-toggle
      v-model="enabled"
      elem-id="trigger-binding-enabled"
      :disabled="!slot.implemented"
      :label="slot.implemented ? t('Ligado') : t('Ligado') + ' — ' + t('em breve')"
      data-testid="trigger-binding-enabled"
    />

    <select-list
      v-model="channelId"
      elem-id="trigger-binding-channel"
      :label="t('Canal')"
      :itens-select="channelOptions"
      :auto-select-first="false"
      :ng-required="true"
      data-testid="trigger-binding-channel"
    />

    <select-list
      v-model="templateId"
      elem-id="trigger-binding-template"
      :label="t('Template')"
      :itens-select="templateOptions"
      :auto-select-first="false"
      :ng-required="true"
      data-testid="trigger-binding-template"
    />

    <select-list-multiple
      v-model="recipientGroups"
      elem-id="trigger-binding-groups"
      :label="t('Destinatários')"
      :itens-select="recipientGroupOptions"
      display="chip"
      data-testid="trigger-binding-groups"
    />

    <p v-if="errorMessage" class="trigger-binding-error" data-testid="trigger-binding-error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useFranchisorChannels } from '../../../composables/useFranchisorChannels'
import type { TriggerSlot } from './TriggerMatrixTable.vue'
import type { FranchisorChannels_TriggerRow } from '../../../contracts/FranchisorChannels_TriggerRow'

const props = defineProps<{
  slot: TriggerSlot
  triggers: FranchisorChannels_TriggerRow[]
}>()

const emit = defineEmits<{
  saved: []
  'validation-failed': []
}>()

const { t } = useI18n()
const { channels, templates, saveTrigger } = useFranchisorChannels()

// Vocabulário curado (docs/billing-franchisor-channels.md "Destinatários") —
// os grupos de staff (Full/Master) ficam de fora por decisão de escopo.
const recipientGroupOptions = [
  { ID: 'Franqueado', Name: t('Franqueado') },
  { ID: 'Gerente', Name: t('Gerente') },
  { ID: 'Operador', Name: t('Operador') },
  { ID: 'Atendente', Name: t('Atendente') },
]

const bindingId = ref<number | null>(null)
const channelId = ref<number | null>(null)
const templateId = ref<number | null>(null)
const recipientGroups = ref<string[]>(['Franqueado'])
const enabled = ref(false)
/** Último `enabled` CONFIRMADO pelo servidor — para onde uma recusa reverte. */
const confirmedEnabled = ref(false)
const errorMessage = ref('')

watch(
  () => props.slot,
  (slot) => {
    const binding = slot?.binding ?? null
    bindingId.value = binding?.id ?? null
    channelId.value = binding?.channel_id ?? null
    templateId.value = binding?.template_id ?? null
    recipientGroups.value = binding?.recipient_groups ?? ['Franqueado']
    enabled.value = binding?.enabled ?? false
    confirmedEnabled.value = binding?.enabled ?? false
    errorMessage.value = ''
  },
  { immediate: true },
)

const varsDisplay = computed(() => {
  const trigger = props.triggers.find((tr) => tr.trigger_key === props.slot?.trigger_key)
  const vars = trigger?.vars ?? []
  return vars.length > 0 ? vars.map((v) => `{{${v}}}`).join(', ') : '-'
})

const channelOptions = computed(() =>
  channels.value
    .filter((c) => c.type === props.slot?.channel_type)
    .map((c) => ({ ID: c.id, Name: c.name ?? `#${c.id}` })),
)

// Só os templates DESTE gatilho e DESTE tipo — o backend recusa
// 'template_channel_type_mismatch', e oferecer a opção impossível convida o erro.
const templateOptions = computed(() =>
  templates.value
    .filter((tpl) => tpl.trigger_key === props.slot?.trigger_key && tpl.channel_type === props.slot?.channel_type)
    .map((tpl) => ({ ID: tpl.id, Name: `${tpl.language} — ${tpl.subject || t('Corpo da mensagem')}` })),
)

const canSave = computed(() => channelId.value != null && templateId.value != null)

async function save(): Promise<void> {
  errorMessage.value = ''
  if (!canSave.value) {
    emit('validation-failed')
    return
  }

  try {
    await saveTrigger({
      ...(bindingId.value != null ? { id: bindingId.value } : {}),
      trigger_key: props.slot.trigger_key,
      channel_id: channelId.value as number,
      template_id: templateId.value as number,
      recipient_groups: recipientGroups.value,
      enabled: enabled.value,
    })
    confirmedEnabled.value = enabled.value
    eventBus.emit('globalMessage.success', t('Vinculação salva'))
    emit('saved')
  } catch (err) {
    const message = (err as Error)?.message ?? ''
    errorMessage.value = message
    enabled.value = confirmedEnabled.value
    log('TriggerBindingForm', 'save', 'Erro ao salvar vinculação', { slot: props.slot?.id, message })
    throw err
  }
}

defineExpose({ canSave, save })
</script>

<style scoped>
.trigger-binding-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.trigger-binding-vars {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.78rem;
  margin: 0;
}

.trigger-binding-error {
  color: #991b1b;
  font-size: 0.78rem;
  margin: 0;
}
</style>
